import React from "react";
import { QuickBooksInvoiceApiError, QuickBooksInvoiceRequest } from "../../../types/quickBooksInvoices";

interface Props {
  status?: QuickBooksInvoiceRequest | null;
  loading?: boolean;
  error?: QuickBooksInvoiceApiError | null;
  onRetry?: () => void | Promise<void>;
  onReplace?: () => void | Promise<void>;
  onRefresh?: () => void;
  retrying?: boolean;
  replacing?: boolean;
}

const copy: Record<string, string> = {
  queued: "Invoice queued. It will be prepared shortly.",
  waiting_dependency: "Invoice is waiting for its required QuickBooks document.",
  preparing: "Preparing invoice…",
  submitting: "Submitting invoice to QuickBooks…",
  retry_wait: "QuickBooks is temporarily unavailable. We’ll retry automatically.",
  reconciling: "Confirming the QuickBooks result…",
  succeeded: "QuickBooks invoice created.",
  failed: "Invoice creation failed.",
  needs_review: "Invoice needs review before it can be completed.",
  blocked: "Invoice is blocked because a required QuickBooks operation failed.",
};

export default function QuickBooksInvoiceStatus({ status, loading, error, onRetry, onReplace, onRefresh, retrying, replacing }: Props) {
  if (!loading && !status && !error) return null;
  const unauthorized = error?.status === 401 || error?.status === 403;
  const message = error ? (unauthorized ? "You are not authorized to manage this invoice." : error.message) : status ? (status.message || copy[status.status]) : "Loading invoice status…";
  const canRetry = Boolean(status?.can_retry && onRetry) && !loading && !retrying && !replacing && !unauthorized;
  const canReplace = Boolean(status?.can_replace && onReplace) && !loading && !retrying && !replacing && !unauthorized;
  return (
    <section role="status" aria-live="polite" aria-atomic="true" className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
      <p>{message}</p>
      {status?.invoice_number && <p>Invoice number: {status.invoice_number}</p>}
      {status?.status === "needs_review" && <p className="mt-1 text-amber-700">A reviewer should confirm this request before retrying.</p>}
      {status?.status === "failed" && !status.can_retry && !status.can_replace && <p className="mt-1 text-amber-700">Contact an administrator to review this request. Creating another invoice or retrying is not permitted.</p>}
      {error && !unauthorized && onRefresh && <button type="button" onClick={onRefresh} disabled={loading || retrying || replacing} className="mt-2 rounded bg-gray-800 px-3 py-1 text-white">Refresh status</button>}
      {canRetry && <button type="button" onClick={() => void onRetry?.()} disabled={retrying} className="mt-2 rounded bg-gray-800 px-3 py-1 text-white">Retry invoice</button>}
      {canReplace && <button type="button" onClick={() => void onReplace?.()} disabled={replacing} className="mt-2 rounded bg-amber-700 px-3 py-1 text-white">Replace invoice</button>}
    </section>
  );
}
